/**
 * AuditDetail Page
 * Detailed view of a single audit record
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Space,
  Tag,
  Button,
  Descriptions,
  Timeline,
  Row,
  Col,
  Alert,

  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  RollbackOutlined,
  FileTextOutlined,
  SettingOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import LoadingSpinner from '../components/LoadingSpinner';
import { auditApi } from '../api/hcApi';
import type { Audit, AuditStatus, Observation } from '../types';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const statusConfig: Record<
  AuditStatus,
  { color: string; icon: React.ReactNode; text: string }
> = {
  pending: { color: 'default', icon: <ClockCircleOutlined />, text: 'Pending' },
  in_progress: { color: 'processing', icon: <SyncOutlined spin />, text: 'In Progress' },
  completed: { color: 'success', icon: <CheckCircleOutlined />, text: 'Completed' },
  failed: { color: 'error', icon: <CloseCircleOutlined />, text: 'Failed' },
};

const AuditDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [audit, setAudit] = useState<Audit | null>(null);
  const [observation, setObservation] = useState<Observation | null>(null);

  useEffect(() => {
    const fetchAudit = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await auditApi.get(id);
        if (response.success && response.data) {
          const auditData = response.data as any;
          setAudit(auditData.audit || auditData);
          if (auditData.observation) {
            setObservation(auditData.observation);
          }
        }
      } catch (err) {
        console.warn('Failed to fetch audit detail:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAudit();
  }, [id]);

  const handleRollback = () => {
    message.loading('Initiating rollback...', 2).then(() => {
      message.success('Rollback completed successfully');
    });
  };

  if (loading) {
    return <LoadingSpinner tip="Loading audit details..." />;
  }

  if (!audit) {
    return (
      <Alert
        message="Audit Not Found"
        description={`No audit found with ID: ${id}`}
        type="error"
        showIcon
        action={
          <Button onClick={() => navigate('/audits')}>Back to Audits</Button>
        }
      />
    );
  }

  const status = statusConfig[audit.status];
  const duration = audit.completed_at
    ? new Date(audit.completed_at).getTime() -
      new Date(audit.started_at).getTime()
    : null;

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/audits')}
          style={{ marginBottom: 16 }}
        >
          Back to Audits
        </Button>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Space>
            <Title level={3} style={{ margin: 0 }}>
              Audit: {audit.id}
            </Title>
            <Tag icon={status.icon} color={status.color}>
              {status.text}
            </Tag>
          </Space>
          {audit.result?.rollback_available && (
            <Button
              danger
              icon={<RollbackOutlined />}
              onClick={handleRollback}
            >
              Rollback Action
            </Button>
          )}
        </div>
      </div>

      <Row gutter={[16, 16]}>
        {/* Main Details */}
        <Col xs={24} lg={16}>
          {/* Audit Info */}
          <Card title="Audit Information" style={{ marginBottom: 16 }}>
            <Descriptions bordered column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Audit ID">{audit.id}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag icon={status.icon} color={status.color}>
                  {status.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Policy">
                {audit.policy_name}
              </Descriptions.Item>
              <Descriptions.Item label="Action">
                <Tag color="blue">{audit.action.toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Started">
                {dayjs(audit.started_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Completed">
                {audit.completed_at
                  ? dayjs(audit.completed_at).format('YYYY-MM-DD HH:mm:ss')
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Duration" span={2}>
                {duration ? `${(duration / 1000).toFixed(2)} seconds` : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Result */}
          <Card
            title={
              <Space>
                <ThunderboltOutlined />
                Action Result
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            {audit.result ? (
              <>
                <Alert
                  message={audit.result.success ? 'Action Successful' : 'Action Failed'}
                  description={audit.result.message}
                  type={audit.result.success ? 'success' : 'error'}
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                {audit.result.details && (
                  <>
                    <Text strong>Details:</Text>
                    <Card
                      size="small"
                      style={{ marginTop: 8, background: '#fafafa' }}
                    >
                      <pre style={{ margin: 0, fontSize: 12 }}>
                        {JSON.stringify(audit.result.details, null, 2)}
                      </pre>
                    </Card>
                  </>
                )}
              </>
            ) : (
              <Text type="secondary">Action is still in progress...</Text>
            )}
          </Card>

          {/* Triggering Observation */}
          {observation && (
            <Card
              title={
                <Space>
                  <FileTextOutlined />
                  Triggering Observation
                </Space>
              }
            >
              <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
                <Descriptions.Item label="Observation ID">
                  {observation.id}
                </Descriptions.Item>
                <Descriptions.Item label="Severity">
                  <Tag
                    color={
                      observation.severity === 'critical'
                        ? 'red'
                        : observation.severity === 'warning'
                        ? 'orange'
                        : 'blue'
                    }
                  >
                    {observation.severity.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Source">
                  {observation.source}
                </Descriptions.Item>
                <Descriptions.Item label="Category">
                  {observation.category}
                </Descriptions.Item>
                <Descriptions.Item label="Metric" span={2}>
                  <Text code>
                    {observation.metric_name}: {observation.metric_value} (threshold:{' '}
                    {observation.threshold_value})
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Description" span={2}>
                  {observation.description}
                </Descriptions.Item>
                <Descriptions.Item label="Timestamp" span={2}>
                  {dayjs(observation.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
        </Col>

        {/* Sidebar */}
        <Col xs={24} lg={8}>
          {/* Timeline */}
          <Card
            title="Execution Timeline"
            style={{ marginBottom: 16 }}
          >
            <Timeline
              items={[
                {
                  color: 'blue',
                  children: (
                    <>
                      <Text strong>Observation Detected</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {observation
                          ? dayjs(observation.timestamp).format('HH:mm:ss')
                          : '-'}
                      </Text>
                    </>
                  ),
                },
                {
                  color: 'blue',
                  children: (
                    <>
                      <Text strong>Policy Matched</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {audit.policy_name}
                      </Text>
                    </>
                  ),
                },
                {
                  color: 'blue',
                  children: (
                    <>
                      <Text strong>Action Started</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {dayjs(audit.started_at).format('HH:mm:ss')}
                      </Text>
                    </>
                  ),
                },
                {
                  color: audit.result?.success ? 'green' : 'red',
                  children: (
                    <>
                      <Text strong>
                        {audit.result?.success
                          ? 'Action Completed'
                          : audit.completed_at
                          ? 'Action Failed'
                          : 'In Progress...'}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {audit.completed_at
                          ? dayjs(audit.completed_at).format('HH:mm:ss')
                          : 'Processing...'}
                      </Text>
                    </>
                  ),
                },
              ]}
            />
          </Card>

          {/* Metadata */}
          {audit.metadata && (
            <Card
              title={
                <Space>
                  <SettingOutlined />
                  Metadata
                </Space>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {Object.entries(audit.metadata).map(([key, value]) => (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Text type="secondary">{key}:</Text>
                    <Text>
                      {typeof value === 'number'
                        ? value.toFixed(2)
                        : String(value)}
                    </Text>
                  </div>
                ))}
              </Space>
            </Card>
          )}

          {/* Quick Actions */}
          <Card title="Quick Actions" style={{ marginTop: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                block
                onClick={() => navigate(`/observations/${audit.observation_id}`)}
              >
                View Observation
              </Button>
              <Button
                block
                onClick={() => navigate(`/policies/${audit.policy_id}`)}
              >
                View Policy
              </Button>
              {audit.result?.rollback_available && (
                <Button block danger onClick={handleRollback}>
                  Rollback Action
                </Button>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AuditDetail;
